import { Link, useParams, useNavigate } from "react-router-dom"
import { useState, useMemo, useCallback, useRef, useEffect } from "react"
import useSWR from "swr"
import { useUploadDocument } from "@/contexts/UploadDocumentContext"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import {
  ArrowLeft,
  Flag,
  Clock,
  DollarSign,
  ArrowUpRight,
  Share2,
  MoreVertical,
  Send,
  Calendar,
  Globe,
  TrendingUp,
  FolderOpen,
  ScrollText,
} from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Badge } from "@/components/ui/badge"
import { Avatar } from "@/components/ui/Avatar"
import { Tabs, TabsList, TabsTab, TabsPanel } from "@/components/ui/tabs"
import LoadingBar from "@/components/Reusable/LoadingBar"
import { cn } from "src/utils/Functions"
import { motion, AnimatePresence } from "motion/react"
import { useDemoDealsDocuments } from "@/hooks/deals/useDemoDealsDocuments"
import { getDealApi } from "src/api/deal"
import type { DealVersion } from "@equaliq/api-model-v2"
import type { DemoParticipant } from "@/types/demo.types"
import type { DemoDeal } from "@/mockData/demoData"
import { PATH_ROOT } from "src/routes/paths"
import { InviteParticipantDialog } from "./components/InviteParticipantDialog"
import {
  WidgetCard,
  DocumentsTable,
  ActionItem,
  ParticipantItem,
  EmptyContractsState,
  KeyTermChart,
} from "./components"
import { useDealPageData } from "@/hooks/deals/useDealPageData"
import type { InviteParticipantFormData } from "@/types/deal.types"

/** Draggable divider between main content and sidebar */
const DragHandle = () => {
  const handleRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handle = handleRef.current
    if (!handle) return

    let startX = 0
    let startWidth = 0

    const onMouseMove = (e: MouseEvent) => {
      e.preventDefault()
      const delta = startX - e.clientX
      const newWidth = Math.min(520, Math.max(280, startWidth + delta))
      document.documentElement.style.setProperty("--deal-sidebar-width", `${newWidth}px`)
    }

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
      document.body.style.cursor = ""
      document.body.style.userSelect = "auto"
    }

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      startX = e.clientX
      const sidebar = handle.nextElementSibling as HTMLElement | null
      startWidth = sidebar?.offsetWidth ?? 380
      document.body.style.cursor = "col-resize"
      document.body.style.userSelect = "none"
      document.addEventListener("mousemove", onMouseMove)
      document.addEventListener("mouseup", onMouseUp)
    }

    handle.addEventListener("mousedown", onMouseDown)
    return () => {
      handle.removeEventListener("mousedown", onMouseDown)
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
    }
  }, [])

  return (
    <div
      ref={handleRef}
      className="hidden lg:flex w-1 cursor-col-resize items-center justify-center hover:bg-primary/20 active:bg-primary/30 transition-colors group shrink-0"
    >
      <div className="w-px h-8 bg-border group-hover:bg-primary/50 transition-colors rounded-full" />
    </div>
  )
}

export interface DealPageProps {
  dealId?: string
  tab?: string
}

const DealPage: React.FC<DealPageProps> = ({ dealId: propDealId }) => {
  const { id: paramId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isDemo = useIsDemo()
  const { demoDeals, demoActionItems, demoParticipants, getDemoContractById } = useDemoDealsDocuments()
  const { dealSummary: DEMO_DEAL_SUMMARY, keyDealTerms: KEY_DEAL_TERMS, exampleActivity: EXAMPLE_ACTIVITY } = useDealPageData()
  const { openDialog } = useUploadDocument()
  const id = propDealId || paramId

  // Fetch real deal in production mode
  const { data: apiDealData, isLoading: apiDealLoading } = useSWR(
    !isDemo && id ? ['deal-page', id] : null,
    () => getDealApi({ dealId: id!, includeVersions: true }),
  )

  const deal: DemoDeal | undefined = useMemo(() => {
    // Demo mode: look up from demo data
    const demoDeal = demoDeals.find((d) => d.id === id)
    if (demoDeal) return demoDeal

    // Production mode: map API response to page shape
    if (!apiDealData) return undefined
    const { deal: apiDeal, versions } = apiDealData
    const versionList = Object.values(versions ?? {}) as DealVersion[]
    const latest = [...versionList].sort((a, b) => b.versionNumber - a.versionNumber)[0]
    const content = latest?.content as { title?: string; description?: string } | undefined

    return {
      id: apiDeal.dealId,
      name: content?.title || `Deal ${apiDeal.dealId.slice(0, 8)}`,
      type: latest?.stage || "drafting",
      status: "In Progress" as const,
      value: "—",
      date: new Date(apiDeal.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      updatedAt: new Date(apiDeal.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      documentCount: 0,
      coreDocument: "—",
    }
  }, [demoDeals, apiDealData, id])

  const [actionItems, setActionItems] = useState(demoActionItems)
  const [newComment, setNewComment] = useState("")
  const [participants, setParticipants] = useState<DemoParticipant[]>(demoParticipants)

  const handleInviteParticipant = useCallback((data: InviteParticipantFormData) => {
    const newParticipant: DemoParticipant = {
      id: `participant-${Date.now()}`,
      name: data.name,
      email: data.email,
      role: data.role,
      permission: data.permission,
      status: "pending",
      invitedAt: new Date().toISOString(),
    }
    setParticipants((prev) => [newParticipant, ...prev])
  }, [])

  const toggleActionItem = useCallback((actionId: string) => {
    setActionItems(items =>
      items.map(item =>
        item.id === actionId ? { ...item, completed: !item.completed } : item
      )
    )
  }, [])

  const { pendingActions, completedActions } = useMemo(() => ({
    pendingActions: actionItems.filter(a => !a.completed),
    completedActions: actionItems.filter(a => a.completed),
  }), [actionItems])

  const handleUpload = useCallback(() => {
    openDialog(id)
  }, [openDialog, id])

  if (!isDemo && apiDealLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingBar />
      </div>
    )
  }

  if (!deal) {
    return (
      <div className="p-8">
        <h1 className="text-foreground text-2xl font-bold">Deal Not Found</h1>
        <p className="text-muted-foreground mt-2">No deal found with ID: {id}</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="size-4 mr-2" />
          Back
        </Button>
      </div>
    )
  }

  const coreContract = deal.coreContractId ? getDemoContractById(deal.coreContractId) : undefined

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <header className="px-4 py-3 sticky top-0 z-10 border-b border-border bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft className="size-4" aria-hidden="true" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-foreground text-lg font-semibold">{deal.name}</h1>
                <Badge variant={deal.status === "Completed" ? "success" : deal.status === "In Progress" ? "info" : "warning"}>
                  {deal.status}
                </Badge>
              </div>
              <p className="text-muted-foreground text-sm">{deal.type} · {deal.value}</p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" aria-label="Share deal">
              <Share2 className="size-4" aria-hidden="true" />
            </Button>
            <Button variant="ghost" size="sm" aria-label="More options">
              <MoreVertical className="size-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="flex h-full">
          {/* Left Column */}
          <div className="flex-1 min-w-0 border-r border-border h-full overflow-y-auto">
            <Tabs defaultValue="overview" className="h-full flex flex-col">
              <div className="p-4">
                <TabsList>
                  <TabsTab value="overview">
                    <ArrowUpRight className="size-4" />
                    Overview
                  </TabsTab>
                  <TabsTab value="documents">
                    <FolderOpen className="size-4" />
                    Documents
                  </TabsTab>
                  {/* <TabsTab value="contracts">
                    <ScrollText className="size-4" />
                    Contracts
                  </TabsTab> */}
                </TabsList>
              </div>

              <TabsPanel value="overview" className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-6">
                  {/* Deal Overview */}
                  <WidgetCard>
                    <WidgetCard.Header>
                      <div className="flex items-center gap-2 pl-2">
                        <ArrowUpRight className="text-muted-foreground size-5" aria-hidden />
                        <h3 className="text-foreground font-semibold">Deal Overview</h3>
                      </div>
                    </WidgetCard.Header>
                    <WidgetCard.Content className="p-2">
                      <div className="flex items-start gap-3 mb-6">
                        <p className="text-foreground text-sm leading-relaxed">
                          This deal covers {DEMO_DEAL_SUMMARY.albumCount} albums with a {DEMO_DEAL_SUMMARY.royaltySplit} royalty split, and you've received {DEMO_DEAL_SUMMARY.advancePercent}% of your advance payment. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.
                        </p>
                      </div>

                      <div className="mb-6 p-3 rounded-xl bg-muted/50">
                        <div className="flex items-start gap-2">
                          <ScrollText className="size-4 text-muted-foreground mt-0.5" aria-hidden="true" />
                          <div>
                            <p className="text-muted-foreground text-xs mb-1">Core Document</p>
                            <p className="text-foreground text-sm font-semibold">{deal.coreDocument}</p>
                            {coreContract && (
                              <Link
                                to={`${isDemo ? PATH_ROOT.demo.analysis : PATH_ROOT.main.analysis}/${coreContract.contractId}`}
                                className="mt-1 text-primary text-xs font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                              >
                                View linked contract: {coreContract.name}
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="p-3 rounded-xl bg-muted/50">
                          <p className="text-muted-foreground text-xs mb-1">Deal Value</p>
                          <p className="text-foreground text-2xl font-semibold tabular-nums">{DEMO_DEAL_SUMMARY.dealValue}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-muted/50">
                          <p className="text-muted-foreground text-xs mb-1">Time Remaining</p>
                          <p className="text-foreground text-2xl font-semibold tabular-nums">
                            {DEMO_DEAL_SUMMARY.progressPercent}%{" "}
                            <span className="text-muted-foreground text-sm font-normal">
                              of {DEMO_DEAL_SUMMARY.totalDays} days
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-3 mb-6">
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                          <Calendar className="size-4 text-muted-foreground" />
                          <div>
                            <p className="text-foreground text-xs font-medium">{DEMO_DEAL_SUMMARY.startDate}</p>
                            <p className="text-muted-foreground text-[10px]">Start</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                          <TrendingUp className="size-4 text-muted-foreground" />
                          <div>
                            <p className="text-foreground text-xs font-medium">{DEMO_DEAL_SUMMARY.targetClose}</p>
                            <p className="text-muted-foreground text-[10px]">Target</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                          <Globe className="size-4 text-muted-foreground" />
                          <div>
                            <p className="text-foreground text-xs font-medium">{DEMO_DEAL_SUMMARY.territory}</p>
                            <p className="text-muted-foreground text-[10px]">Territory</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                          <Clock className="size-4 text-muted-foreground" />
                          <div>
                            <p className="text-foreground text-xs font-medium">{DEMO_DEAL_SUMMARY.duration}</p>
                            <p className="text-muted-foreground text-[10px]">Duration</p>
                          </div>
                        </div>
                      </div>
                    </WidgetCard.Content>
                  </WidgetCard>

                  {/* Key Deal Terms */}
                  <WidgetCard>
                    <WidgetCard.Header>
                      <div className="flex items-center gap-2 pl-2">
                        <DollarSign className="text-muted-foreground size-5" aria-hidden />
                        <h3 className="text-foreground font-semibold">Key Deal Terms</h3>
                      </div>
                      <Button variant="secondary" size="sm" className="text-xs">
                        View Full Contract Analysis
                      </Button>
                    </WidgetCard.Header>
                    <WidgetCard.Content className="p-1">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {KEY_DEAL_TERMS.map((term) => (
                          <div
                            key={term.id}
                            className="flex flex-col gap-4 p-3 rounded-xl bg-muted/50 min-h-80"
                          >
                            <KeyTermChart termId={term.id} />
                            <div>
                              <term.icon className={cn(
                                "size-5 mb-2",
                                term.type === "risk" ? "text-red-400" : "text-muted-foreground"
                              )} />
                              <p className="text-muted-foreground text-xs">{term.title}</p>
                              <p className="text-lg font-semibold text-foreground">{term.value}</p>
                              <p className="text-muted-foreground/70 text-xs mt-1">{term.detail}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </WidgetCard.Content>
                  </WidgetCard>
                </div>
              </TabsPanel>

              <TabsPanel value="documents" className="flex-1 overflow-y-auto">
                <div className="p-6 h-full">
                  <DocumentsTable onUpload={handleUpload} />
                </div>
              </TabsPanel>

              <TabsPanel value="contracts" className="flex-1 overflow-y-auto">
                {EmptyContractsState}
              </TabsPanel>
            </Tabs>
          </div>

          {/* Resize Handle */}
          <DragHandle />

          {/* Right Column */}
          <div className="h-full overflow-y-auto" style={{ width: "var(--deal-sidebar-width, 380px)", minWidth: 280, maxWidth: 520 }}>
            <div className="p-5 space-y-5">
              {/* Action Items */}
              <WidgetCard>
                <WidgetCard.Header>
                  <div className="flex items-center gap-2 pl-2">
                    <Flag className="text-muted-foreground size-5" aria-hidden />
                    <h3 className="text-foreground font-semibold">Action Items</h3>
                    {pendingActions.length > 0 && (
                      <Badge variant="warning" size="sm">{pendingActions.length}</Badge>
                    )}
                  </div>
                </WidgetCard.Header>
                <WidgetCard.Content>
                  <motion.div className="space-y-1.5">
                    <AnimatePresence mode="popLayout">
                      {pendingActions.map((action) => (
                        <ActionItem key={action.id} action={action} onToggle={toggleActionItem} />
                      ))}
                    </AnimatePresence>

                    {completedActions.length > 0 && (
                      <div className={cn("pt-2 mt-2", completedActions.length > 1 && "border-t border-border/50")}>
                        <p className="text-muted-foreground text-[10px] uppercase tracking-wider mb-1.5">Completed ({completedActions.length})</p>
                        {completedActions.map((action) => (
                          <ActionItem key={action.id} action={action} onToggle={toggleActionItem} isCompleted />
                        ))}
                      </div>
                    )}
                  </motion.div>
                </WidgetCard.Content>
              </WidgetCard>

              {/* Participants */}
              <WidgetCard>
                <WidgetCard.Header>
                  <div className="flex items-center gap-2 pl-2">
                    <h3 className="text-foreground font-semibold">Participants</h3>
                  </div>
                  <InviteParticipantDialog
                    dealName={deal.name}
                    onInvite={handleInviteParticipant}
                  />
                </WidgetCard.Header>
                <WidgetCard.Content>
                  <motion.div layout className="space-y-2.5">
                    <AnimatePresence mode="popLayout">
                      {participants.map((participant) => (
                        <ParticipantItem key={participant.id} participant={participant} />
                      ))}
                    </AnimatePresence>
                  </motion.div>
                </WidgetCard.Content>
              </WidgetCard>

              {/* Recent Activity */}
              <WidgetCard>
                <WidgetCard.Header>
                  <div className="flex items-center gap-2 pl-2">
                    <Clock className="text-muted-foreground size-5" aria-hidden />
                    <h3 className="text-foreground font-semibold">Recent Activity</h3>
                  </div>
                </WidgetCard.Header>
                <WidgetCard.Content>
                  <div className="flex gap-2 mb-4">
                    <Avatar fallback="Y" className="size-6 shrink-0" />
                    <div className="flex-1">
                      <label htmlFor="deal-comment" className="sr-only">Add a comment</label>
                      <input
                        id="deal-comment"
                        name="comment"
                        type="text"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment…"
                        autoComplete="off"
                        className="w-full bg-muted/50 text-foreground placeholder:text-muted-foreground/50 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </div>
                    <Button size="sm" variant="ghost" disabled={!newComment.trim()} className="h-7 w-7 p-0" aria-label="Send comment">
                      <Send className="size-3.5" aria-hidden="true" />
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {EXAMPLE_ACTIVITY.map((activity, index) => (
                      <div key={activity.id} className="flex gap-2.5">
                        <div className="flex flex-col items-center">
                          <Avatar
                            src={activity.avatar}
                            fallback={activity.user.charAt(0)}
                            className="size-6 shrink-0"
                          />
                          {index < EXAMPLE_ACTIVITY.length - 1 && (
                            <div className="w-px flex-1 bg-border mt-1.5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 pb-3">
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-foreground font-medium text-xs">{activity.user}</span>
                            <span className="text-muted-foreground text-[10px]">{activity.action}</span>
                            <span className="text-muted-foreground/50 text-[10px] ml-auto">{activity.timestamp}</span>
                          </div>
                          {activity.action === "commented" ? (
                            <div className="mt-1 p-2 rounded-md bg-muted/50 text-foreground/70 text-xs leading-relaxed">
                              {activity.content}
                            </div>
                          ) : (
                            <p className="text-muted-foreground text-xs mt-0.5">{activity.content}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </WidgetCard.Content>
              </WidgetCard>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DealPage
